import React, { FC, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    LinearProgress,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSettings } from 'context/app-settings';
import { switchProgress, useDataSetSwitch } from 'context/data-set-switch';
import { DATA_SET_BY_DB_MODE, DataSet, DataSetSummary } from 'models/data-set-switch';
import { formatDate } from 'utils/format';

const DATA_SET_ORDER: DataSet[] = ['REGRESSION', 'PRODUCTION_COPY'];

/**
 * Switches staging between the frozen regression data and the masked
 * production copy. The switch itself runs on the server; progress and the
 * finished/failed message come from DataSetSwitchProvider.
 */
const DbSwitchSettings: FC = () => {
    const { t: translate } = useTranslation(['app-settings', 'common']);
    const { appSettings } = useAppSettings();
    const { status, dataSets, isRunning, starting, startSwitch } = useDataSetSwitch();
    const [confirming, setConfirming] = useState<DataSet | null>(null);

    const activeDataSet = appSettings.activeDbMode ? DATA_SET_BY_DB_MODE[appSettings.activeDbMode] : undefined;
    const summaryOf = (dataSet: DataSet): DataSetSummary | undefined =>
        dataSets.find((summary) => summary.dataSet === dataSet);

    const label = (dataSet: DataSet) =>
        dataSet === 'PRODUCTION_COPY'
            ? translate('app-settings:DATA_SET_PRODUCTION_COPY')
            : translate('app-settings:DATA_SET_REGRESSION');

    const activeLabel = () => {
        if (!activeDataSet) return translate('app-settings:DATA_SET_UNKNOWN');
        if (activeDataSet === 'PRODUCTION_COPY') {
            return translate('app-settings:DATA_SET_PRODUCTION_COPY_WITH_DATE', {
                date: formatDate(appSettings.activeDataSetSnapshotDate, 'date') || '?',
            });
        }
        return label(activeDataSet);
    };

    const confirm = () => {
        if (confirming) startSwitch(confirming);
        setConfirming(null);
    };

    const progress = status && isRunning ? switchProgress(status) : undefined;
    const showLastFailure = status && !isRunning && (status.state === 'FAILED' || status.state === 'INTERRUPTED');

    return (
        <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h1" gutterBottom>
                {translate('app-settings:DB_SWITCH_TITLE')}
            </Typography>

            <Alert severity={activeDataSet === 'PRODUCTION_COPY' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                {isRunning
                    ? translate('app-settings:DATA_SET_ACTIVE_SWITCHING')
                    : translate('app-settings:DATA_SET_ACTIVE', { dataSet: activeLabel() })}
            </Alert>

            <Typography sx={{ mb: 3 }}>{translate('app-settings:DB_SWITCH_DESCRIPTION')}</Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {DATA_SET_ORDER.map((dataSet) => {
                    const summary = summaryOf(dataSet);
                    const isActive = dataSet === activeDataSet;
                    const unavailable = summary ? !summary.available : false;
                    return (
                        <Box key={dataSet}>
                            <Button
                                variant="contained"
                                disabled={isActive || unavailable || isRunning || starting}
                                onClick={() => setConfirming(dataSet)}
                                sx={{ whiteSpace: 'nowrap' }}
                                data-test={`db-switch.${dataSet}`}
                            >
                                {label(dataSet)}
                            </Button>
                            {dataSet === 'PRODUCTION_COPY' && summary?.snapshotDate && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {translate('app-settings:DATA_SET_SNAPSHOT_FROM', {
                                        date: formatDate(summary.snapshotDate, 'date'),
                                    })}
                                </Typography>
                            )}
                            {unavailable && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {translate('app-settings:DATA_SET_UNAVAILABLE')}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </Stack>

            {status && isRunning && (
                <Box data-test="db-switch.progress">
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        {translate('app-settings:DATA_SET_SWITCH_PROGRESS', {
                            dataSet: label(status.dataSet),
                            step: translate(`app-settings:DATA_SET_SWITCH_STEP_${status.step}`),
                        })}
                        {status.currentCollection ? ` (${status.currentCollection})` : ''}
                    </Typography>
                    <LinearProgress
                        variant={progress === undefined ? 'indeterminate' : 'determinate'}
                        value={progress}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {translate('app-settings:DATA_SET_SWITCH_STARTED_BY', {
                            name: status.startedBy,
                            time: formatDate(status.startedAt, 'time'),
                        })}
                    </Typography>
                </Box>
            )}

            {showLastFailure && (
                <Alert severity="error">
                    {status.state === 'INTERRUPTED'
                        ? translate('app-settings:DATA_SET_SWITCH_INTERRUPTED')
                        : translate('app-settings:DATA_SET_SWITCH_FAILED', { error: status.error ?? '' })}
                </Alert>
            )}

            <Dialog open={!!confirming} onClose={() => setConfirming(null)}>
                <DialogTitle>
                    {confirming &&
                        translate('app-settings:DATA_SET_CONFIRM_TITLE', {
                            dataSet: label(confirming),
                        })}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>{translate('app-settings:DATA_SET_CONFIRM_BODY')}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" onClick={() => setConfirming(null)}>
                        {translate('common:CANCEL')}
                    </Button>
                    <Button onClick={confirm} variant="contained" color="warning" data-test="db-switch.confirm">
                        {translate('app-settings:DATA_SET_CONFIRM_ACTION')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default DbSwitchSettings;
